/**
 * Custom CSS drawer (Phase 6 of pb-section-properties overhaul).
 *
 * Replaces the legacy plain `<textarea>` with the same `mountCodeEditor()`
 * already used by the element-level glass panel
 * (`builder/scripts/editing/glass-panel-section.js:366-410`):
 *
 *   - CodeMirror with line numbers + monospace + theme-token completion
 *   - Validity callback that natively accepts both rules
 *     (`.cs-title { color: red }`) and bare declarations (`color: red`),
 *     so the section CSS textarea hint stays accurate
 *   - Internal debounce + idle-commit so the store doesn't see a transaction
 *     per keystroke; the editor manages its own transaction lifecycle
 *   - Undo / redo bindings that round-trip through the store
 *
 * Sectional changes vs. the element-level mount:
 *
 *   - Path is `pages.{p}.sections.{s}.overrides.customCSS` (section-level,
 *     not element-level); resolved fresh on every onChange so a page switch
 *     mid-edit doesn't write into the wrong section.
 *   - Live keystroke updates use direct `store.update + theme:override-changed`
 *     (lightweight, matches the element-level pattern). The bridge sync
 *     fires only on Reset (explicit user action, full five-emission contract
 *     via `applySectionOverride`) so typing a hundred keystrokes doesn't
 *     emit a hundred `query-section-positions` calls. Custom CSS changes
 *     don't typically affect section spacing badges, so the deferred sync
 *     is fine; the next selection change will refresh anyway.
 *   - State-changed listener re-syncs the editor buffer after undo / redo /
 *     programmatic store mutations from outside the drawer.
 *
 * Returns a teardown that destroys the editor and detaches event listeners.
 */

import {
  buildAppliedCSSText,
  mountCodeEditor,
  mountReadOnlyEditor
} from '../../editing/code-view-editor.js';
import { loadCsClassIndex } from '../../editing/cs-class-index-loader.js';
import { getCurrentTheme } from '../../theme/theme-editor.js';
import { applySectionOverride } from './apply-section-override.js';
import { setCustomCssSummaryValidity } from './row-summaries.js';
import { eventBus } from '../../core/events.js';
import { store } from '../../core/state.js';
import { attachTablistNav } from '../tablist-nav.js';

function resolvePageSection(instanceId) {
  const page = store.getActivePage();
  if (!page || !Array.isArray(page.sections)) return null;
  const sectionIdx = page.sections.findIndex((s) => s.instanceId === instanceId);
  if (sectionIdx === -1) return null;
  const state = store.getState();
  const pageIdx = state && Array.isArray(state.pages) ? state.pages.indexOf(page) : -1;
  if (pageIdx === -1) return null;
  return {
    section: page.sections[sectionIdx],
    customCssPath: `pages.${pageIdx}.sections.${sectionIdx}.overrides.customCSS`
  };
}

/**
 * Mount the Custom CSS drawer body into `container`.
 *
 * @param {HTMLElement} container - drawer body element
 * @param {{ instanceId: string }} ctx
 * @returns {() => void} teardown function (drawer.js invokes it on swap/close)
 */
export function renderCustomCssDrawer(container, { instanceId }) {
  if (!container || !instanceId) {
    console.warn('[css-drawer] missing container/instanceId');
    return () => {};
  }

  // Reuse the element-level CSS panel's class names — they're already styled
  // in `builder/styles/glass-panels.css`, so the drawer body looks identical
  // to the element-level CSS editor and inherits future polish for free.
  container.innerHTML = `
    <div class="pb-spi-css-drawer pb-spi-drawer-section" data-css-drawer>
      <div class="pb-gp-segmented pb-gp-segmented--inner" data-css-drawer-tabs role="tablist" aria-label="Section CSS views">
        <button type="button" class="pb-gp-active" data-css-drawer-tab="custom" role="tab" aria-selected="true">Custom CSS</button>
        <button type="button" data-css-drawer-tab="applied" role="tab" aria-selected="false">Applied CSS</button>
      </div>
      <div class="pb-gp-code-subtab-body" data-css-drawer-tab-body="custom">
        <div class="pb-gp-code-header pb-spi-css-drawer-header">
          <div class="pb-gp-code-title">
            <span class="pb-gp-code-label">Custom CSS</span>
            <span class="pb-gp-code-hint">Selectors auto-scope to <code>[data-pb-instance]</code>.</span>
          </div>
          <button class="pb-gp-code-reset pb-spi-css-drawer-reset" type="button" data-css-drawer-reset>Reset</button>
        </div>
        <div class="pb-gp-code-editor" data-css-drawer-editor aria-label="Section custom CSS editor"></div>
        <div class="pb-gp-code-warning" data-css-drawer-warning hidden></div>
      </div>
      <div class="pb-gp-code-subtab-body pb-gp-code-subtab-body--hidden" data-css-drawer-tab-body="applied">
        <div class="pb-gp-code-header pb-spi-css-drawer-header">
          <div class="pb-gp-code-title">
            <span class="pb-gp-code-label">Applied CSS (read-only)</span>
          </div>
          <button class="pb-gp-code-copy-selection" type="button" data-css-drawer-copy-selection disabled>Copy to Custom</button>
        </div>
        <div class="pb-gp-applied-editor" data-css-drawer-applied-editor aria-label="Section applied CSS viewer (read-only)"></div>
      </div>
    </div>
  `;

  const tabButtons = Array.from(container.querySelectorAll('[data-css-drawer-tab]'));
  let tabNav = null;
  const tabBodies = Array.from(container.querySelectorAll('[data-css-drawer-tab-body]'));
  const editorEl = container.querySelector('[data-css-drawer-editor]');
  const appliedEditorEl = container.querySelector('[data-css-drawer-applied-editor]');
  const warningEl = container.querySelector('[data-css-drawer-warning]');
  const resetBtn = container.querySelector('[data-css-drawer-reset]');
  const copyBtn = container.querySelector('[data-css-drawer-copy-selection]');

  // Mount-token pattern (mirrors `_ensureCodeEditor` in glass-panel-section.js):
  // CodeMirror loads lazily, so the drawer can be closed / swapped before the
  // mount finishes. The token + isMountCurrent check make stale mounts no-op.
  let mountToken = 0;
  let appliedMountToken = 0;
  let editor = null;
  let appliedEditor = null;
  let appliedRefreshQueued = false;
  let teardownInvoked = false;

  const initialResolved = resolvePageSection(instanceId);
  const initialValue = initialResolved?.section?.overrides?.customCSS ?? '';

  function refreshResetEnabled() {
    if (!resetBtn) return;
    const resolved = resolvePageSection(instanceId);
    const stored = resolved?.section?.overrides?.customCSS ?? '';
    const local = editor?.getValue?.() ?? stored;
    const hasContent = !!(stored || local);
    resetBtn.disabled = !hasContent;
    resetBtn.classList.toggle('pb-gp-code-reset-active', hasContent);
  }

  async function ensureCustomEditor({ focus = false } = {}) {
    if (editor && !editor.isDestroyed?.()) {
      if (focus) {
        try {
          editor.focusEnd?.();
        } catch {
          /* noop */
        }
      }
      return editor;
    }
    if (!editorEl) return null;

    mountToken += 1;
    const token = mountToken;
    const classIndex = await loadCsClassIndex();
    if (teardownInvoked || mountToken !== token || !editorEl.isConnected) return null;

    const theme = getCurrentTheme() || {};
    const resolved = resolvePageSection(instanceId);
    const mountInitialValue = resolved?.section?.overrides?.customCSS ?? initialValue;
    let mounted = null;
    mounted = await mountCodeEditor({
      container: editorEl,
      initialValue: mountInitialValue,
      classIndex,
      cssVariables: Object.keys(theme),
      isMountCurrent: () => !teardownInvoked && mountToken === token && editorEl.isConnected,
      onTransactionStart: () => store.beginTransaction('Edit section CSS'),
      onChange: (value) => {
        // Re-resolve on every change — the active page can shift via toolbar
        // or undo before the editor's debounce flushes.
        const next = resolvePageSection(instanceId);
        if (!next) return;
        store.update(next.customCssPath, value);
        eventBus.emit('theme:override-changed');
        refreshResetEnabled();
      },
      onCommit: () => {
        store.commitTransaction();
        refreshResetEnabled();
      },
      onUndo: () => {
        store.undo();
        eventBus.emit('theme:override-changed');
        syncFromState();
      },
      onRedo: () => {
        store.redo();
        eventBus.emit('theme:override-changed');
        syncFromState();
      },
      onValidityChange: ({ ok, message }) => {
        const value = editor?.getValue?.() ?? mounted?.getValue?.() ?? '';
        setCustomCssSummaryValidity(instanceId, {
          ok,
          message,
          lineCount: value ? value.split(/\r?\n/).length : 0
        });
        eventBus.emit('section-properties:css-validity-changed', { instanceId });
        if (!warningEl) return;
        warningEl.hidden = !!ok;
        warningEl.textContent = ok
          ? ''
          : message || 'CSS looks incomplete; preview is paused until input parses cleanly.';
      }
    });

    if (teardownInvoked || mountToken !== token) {
      try {
        mounted?.destroy?.();
      } catch {
        /* noop */
      }
      return null;
    }

    editor = mounted;
    if (focus) {
      try {
        editor?.focusEnd?.();
      } catch {
        /* noop */
      }
    }
    refreshResetEnabled();
    return editor;
  }

  async function ensureAppliedEditor() {
    if (appliedEditor && !appliedEditor.isDestroyed?.()) {
      refreshAppliedEditor();
      return appliedEditor;
    }
    if (!appliedEditorEl) return null;
    const resolved = resolvePageSection(instanceId);
    if (!resolved) return null;

    appliedMountToken += 1;
    const token = appliedMountToken;
    const mounted = await mountReadOnlyEditor({
      container: appliedEditorEl,
      value: buildAppliedCSSText(resolved.section),
      isMountCurrent: () =>
        !teardownInvoked && appliedMountToken === token && appliedEditorEl.isConnected,
      onSelectionChange: (hasSelection) => {
        if (copyBtn) copyBtn.disabled = !hasSelection;
      }
    });

    if (teardownInvoked || appliedMountToken !== token) {
      try {
        mounted?.destroy?.();
      } catch {
        /* noop */
      }
      return null;
    }

    appliedEditor = mounted;
    return appliedEditor;
  }

  function refreshAppliedEditor() {
    if (!appliedEditor || appliedEditor.isDestroyed?.()) return;
    const resolved = resolvePageSection(instanceId);
    if (!resolved) return;
    appliedEditor.setValue?.(buildAppliedCSSText(resolved.section));
  }

  function scheduleAppliedRefresh() {
    if (!appliedEditor || appliedEditor.isDestroyed?.() || appliedRefreshQueued) return;
    appliedRefreshQueued = true;
    const schedule =
      typeof requestAnimationFrame === 'function'
        ? requestAnimationFrame
        : (callback) => setTimeout(callback, 0);
    schedule(() => {
      appliedRefreshQueued = false;
      if (teardownInvoked) return;
      refreshAppliedEditor();
    });
  }

  async function setActiveTab(tab, { focusCustom = false } = {}) {
    const nextTab = tab === 'applied' ? 'applied' : 'custom';
    for (const btn of tabButtons) {
      const active = btn.dataset.cssDrawerTab === nextTab;
      btn.classList.toggle('pb-gp-active', active);
      btn.setAttribute('aria-selected', String(active));
    }
    tabNav?.sync();
    for (const body of tabBodies) {
      body.classList.toggle(
        'pb-gp-code-subtab-body--hidden',
        body.dataset.cssDrawerTabBody !== nextTab
      );
    }

    if (nextTab === 'applied') {
      await ensureAppliedEditor();
    } else {
      await ensureCustomEditor({ focus: focusCustom });
    }
  }

  async function copyAppliedSelection() {
    const selectedText = appliedEditor?.getSelection?.().trim();
    if (!selectedText) return;
    await setActiveTab('custom');
    const customEditor = await ensureCustomEditor();
    customEditor?.commitPending?.();
    const resolved = resolvePageSection(instanceId);
    if (!resolved) return;

    const current = resolved.section.overrides?.customCSS || '';
    const appended = current
      ? `${current}\n\n/* copied from Applied CSS */\n${selectedText}`
      : `/* copied from Applied CSS */\n${selectedText}`;

    store.beginTransaction('Copy applied CSS to overrides');
    store.update(resolved.customCssPath, appended);
    eventBus.emit('theme:override-changed');
    store.commitTransaction();
    customEditor?.setValue?.(appended);
    customEditor?.focusEnd?.();
    refreshResetEnabled();
    scheduleAppliedRefresh();
  }

  resetBtn?.addEventListener('click', () => {
    // Flush any in-flight debounced edit so the editor's internal pendingValue
    // doesn't race with our explicit reset write.
    editor?.commitPending?.();
    const resolved = resolvePageSection(instanceId);
    if (!resolved) return;
    const current = resolved.section.overrides?.customCSS ?? '';
    const localValue = editor?.getValue?.() ?? current;
    if (!current && !localValue) return;
    // Explicit reset → full five-emission contract via applySectionOverride.
    // (Typing path uses direct store.update for perf; reset is rare and benefits
    // from canvas-overlay refresh.)
    applySectionOverride(instanceId, 'customCSS', '', { source: 'css-drawer-reset' });
    editor?.setValue?.('');
    if (warningEl) {
      warningEl.hidden = true;
      warningEl.textContent = '';
    }
    refreshResetEnabled();
    scheduleAppliedRefresh();
  });

  // ── Async mount ────────────────────────────────────────────────────────
  ensureCustomEditor({ focus: true }).catch((err) => {
    console.error('[css-drawer] mountCodeEditor failed:', err);
  });

  for (const btn of tabButtons) {
    btn.addEventListener('click', () => {
      void setActiveTab(btn.dataset.cssDrawerTab || 'custom');
    });
  }
  // BC-11: arrow keys, Home/End and one tab stop for the drawer's tablist.
  const tablistEl = container.querySelector('[data-css-drawer-tabs]');
  tabNav = tablistEl
    ? attachTablistNav(tablistEl, { onActivate: (button) => button.click() })
    : null;

  copyBtn?.addEventListener('click', () => {
    void copyAppliedSelection();
  });

  // ── Sync editor on external state changes (undo/redo / programmatic) ──
  function syncFromState() {
    if (!editor || editor.isDestroyed?.()) return;
    const resolved = resolvePageSection(instanceId);
    const next = resolved?.section?.overrides?.customCSS ?? '';
    if (editor.getValue?.() !== next) {
      editor.setValue?.(next);
    }
    refreshResetEnabled();
  }

  const onOverrideChanged = ({ instanceId: id, field } = {}) => {
    if (id && id !== instanceId) return;
    if (field && field !== 'customCSS') return;
    syncFromState();
    scheduleAppliedRefresh();
  };
  // `state-changed` fires for undo / redo / direct store.update from any path
  // (including the editor's own onChange). The editor's debounce ensures we
  // don't loop here — getValue() === next short-circuits the setValue.
  const onStateChanged = () => {
    syncFromState();
    scheduleAppliedRefresh();
  };

  eventBus.on('theme:override-changed', onOverrideChanged);
  eventBus.on('state-changed', onStateChanged);

  // ── Teardown ───────────────────────────────────────────────────────────
  return function teardown() {
    teardownInvoked = true;
    mountToken += 1; // invalidate any pending async mount
    appliedMountToken += 1;
    appliedRefreshQueued = false;
    eventBus.off('theme:override-changed', onOverrideChanged);
    eventBus.off('state-changed', onStateChanged);
    setCustomCssSummaryValidity(instanceId, null);
    eventBus.emit('section-properties:css-validity-changed', { instanceId });
    // Flush any pending debounced write so the user's last keystroke isn't
    // lost when the drawer is closed mid-edit; the parent drawer's
    // `beforeClose` already calls `store.commitTransaction()`, which closes
    // the transaction this flush opens (or no-ops if there was nothing pending).
    try {
      editor?.commitPending?.();
    } catch {
      /* noop */
    }
    try {
      editor?.destroy?.();
    } catch {
      /* noop */
    }
    try {
      appliedEditor?.destroy?.();
    } catch {
      /* noop */
    }
    editor = null;
    appliedEditor = null;
  };
}
